import React, { useContext, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  FlexMiddle,
  FlexSpace,
  FieldsTypes as BaseFieldsTypes,
  FormModal,
  ToggleField,
  ExplanationTooltip
} from '@ubeya/shared-web/components';
import useFormFields from '@ubeya/shared-web/hooks/useFormFields';
import { responsiveFields } from '@ubeya/shared-web/style';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import PrivilegesContext from '@ubeya/shared-web/contexts/PrivilegesContext';
import privileges from '@ubeya/shared-web/privileges';

const FieldsTypes = styled(BaseFieldsTypes)`
  padding: 33px;
  flex-direction: ${({ $columnView }) => ($columnView ? 'column' : 'row')};
  justify-content: space-around;
  > div:first-child {
    display: none;
  }
`;

const Container = styled(FlexSpace)`
  ${responsiveFields.tablet}
  padding: 34px 42px;
  overflow: auto;
`;

const SensitiveDataRow = styled(FlexMiddle)`
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid ${({ theme }) => theme.colors.grey2};
  gap: 8px;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: flex-start;
`;

const SensitiveToggleWrap = styled.div`
  flex: 0 1 auto;
  min-width: 0;

  .label-wrapper {
    width: fit-content;
    max-width: 100%;
  }
`;

const SensitiveHelpWrap = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
`;

const FieldModal = ({
  columnView = true,
  selectedField,
  onClose,
  onConfirm,
  hideFieldExpiration = false,
  showLimitEditAdminsToggle = false,
  hideTextLimit = false,
  allowedFieldTypes,
  isCompliance = false,
  showEmployeeSensitiveOption = false
}) => {
  const { t } = useContext(ConfigContext);
  const hasSensitiveDataAccess = !!useContext(PrivilegesContext)[privileges.SENSITIVE_DATA_ACCESS];

  const { fieldsMap } = useFormFields();

  const [fieldTypeId, setFieldTypeId] = useState(selectedField?.fieldTypeId);

  const { slug, FormComponent: Component, initialValues: fieldInitialValues, allowMultiple, content } =
    fieldsMap[fieldTypeId] || {};

  const initialValues = useMemo(
    () => ({
      ...fieldInitialValues,
      ...selectedField,
      isSensitive: !!selectedField?.isSensitive
    }),
    [fieldInitialValues, selectedField]
  );

  const isCreateMode = !initialValues?.id;
  const showSensitiveDataToggle =
    showEmployeeSensitiveOption && hasSensitiveDataAccess && !isCompliance && isCreateMode && !selectedField?.isGeneric;

  const componentProps = useMemo(
    () => ({
      hideExpiration: hideFieldExpiration,
      editMode: !!initialValues?.id,
      showLimitEditAdminsToggle,
      hideTextLimit
    }),
    [hideFieldExpiration, initialValues?.id, showLimitEditAdminsToggle, hideTextLimit]
  );

  return (
    <FormModal
      initialValues={initialValues}
      onSubmit={async (values) => {
        const { isSensitive, ...valuesRest } = values;
        const ignoreEmptyOptions = {
          fieldTypeId,
          ...valuesRest,
          allowMultiple: allowMultiple === undefined ? values.allowMultiple : allowMultiple,
          options: values.options?.filter?.(({ title }) => !!title),
          ...(showEmployeeSensitiveOption ? { isSensitive: !!isSensitive } : {})
        };
        await onConfirm(ignoreEmptyOptions);
        onClose();
      }}
      onClose={onClose}
      size={columnView ? 'xs' : 'small'}
      title={`${t(slug)} ${content ? '' : t('field')}`}
      showConfirmButton={!!fieldTypeId}
      confirmText={selectedField.id ? t('update') : t('add')}
      isPaddingContent={false}
      disabledOnInvalid
      disabledOnPristine={false}>
      {!fieldTypeId ? (
        <FieldsTypes
          onClick={(value) => setFieldTypeId(value)}
          includeOnlyAdminSupported
          $columnView={columnView}
          allowedFieldTypes={allowedFieldTypes}
          isCompliance={isCompliance}
        />
      ) : (
        <Container>
          <Component {...componentProps} />
          {showSensitiveDataToggle ? (
            <SensitiveDataRow>
              <SensitiveToggleWrap>
                <ToggleField name="isSensitive" text={t('sensitiveData')} />
              </SensitiveToggleWrap>
              <SensitiveHelpWrap>
                <ExplanationTooltip body={t('sensitiveDataFieldCreateHelp')} />
              </SensitiveHelpWrap>
            </SensitiveDataRow>
          ) : null}
        </Container>
      )}
    </FormModal>
  );
};

export default FieldModal;
